/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class VisDnD
 * @hideconstructor
 *
 * @classdesc Handles drag and drop of metadata to create a visualization.
 */
define([
	'../../../../../lib/@waca/dashboard-common/dist/api/DnDProviderAPI',
	'../../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'underscore'
], (
	DnDProviderAPI,
	APIFactory,
	_
) => {

	const METADATA_TYPES = [
		'MODEL_ITEM',
		'GRID_HEADER_ITEM'
	];

	return class MetadataDnD {

		constructor(options = {}) {
			this.features = options.features;
			const canvasDnD = this.features['CanvasDnD'];

			if (!canvasDnD){
				throw new Error('MetadataDnD should depend on the CanvasDnD dashboard feature');
			}

			canvasDnD.registerProvider(this.getAPI());
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [DnDProviderAPI]);
			}
			return this.api;
		}

		destroy () {
			this.api = null;
		}

		accepts (payload, options) {
			return MetadataDnD.canAccepts(payload, options);
		}

		static canAccepts (payload = {}, options = {}) {
			const isValid = payload.data && payload.data.utils && payload.data.utils.isValid(options);
			return isValid && METADATA_TYPES.includes(payload.type) ? true : false;
		}

		onDrop (payload = {}) {
			if (!payload.data || !METADATA_TYPES.includes(payload.type)) {
				return null;
			}

			const dataItems = [];
			const localFilters = [];
			const droppedColumns = payload.data.columns ? payload.data.columns : [];
			let originalMetadataColumns = _.map(droppedColumns, (column) => column.original.metadataColumn);
			const navPathIds = _.reduce(originalMetadataColumns, (result, item) => {
				const parent = item.getParent();
				const isDrillGroup = parent && parent.getObjectType() === 'DrillGroup';
				const navPathId = (isDrillGroup && typeof parent.getIdentifier === 'function') ? parent.getId() : null;
				const itemId = item.getId();

				if(navPathId && itemId) {
					const currentNavPathID = {};
					currentNavPathID[itemId] = navPathId;
					return _.defaults(result, currentNavPathID);
				} else {
					return result;
				}
			}, {});

			let metadataColumns = _.map(droppedColumns, (column) => column.metadataColumn);

			const columnIds = [];
			metadataColumns.forEach(metadataColumn => {
				if (metadataColumn.isFilter()){
					localFilters.push({
						id: metadataColumn.getId(),
						columnId: metadataColumn.getId()
					});
				} else {
					columnIds.push(metadataColumn.getId());
					dataItems.push({
						itemId: metadataColumn.getId(),
						itemLabel: metadataColumn.getLabel()
					});
				}
			});


			//Now add selected members as local filters
			const columnsWithMembers = payload.data.utils.getColumnsWithMembers();

			metadataColumns.forEach(metadataColumn => {
				const useAsLocalFilters = columnsWithMembers[metadataColumn.getId()];
				if (useAsLocalFilters && useAsLocalFilters.length > 0) {
					const filter = {
						id: metadataColumn.getId(),
						columnId: metadataColumn.getId(),
						overrideHierarchyDefaultSelection: true,
						excludedValues: [],
						operator: 'in',
						type: null,
						values: []
					};
					useAsLocalFilters.forEach(value => {
						filter.values.push({
							d: value.memberCaption,
							u: value.id
						});
					});

					localFilters.push(filter);
				}
			});

			const result =  {
				type: 'live',
				data: {
					dataViews: [{
						modelRef: payload.data.sourceId,
						dataItems: dataItems
					}]
				}
			};

			// Go over the dataitems and insert the navpath id
			result.data.dataViews.forEach(dataView => {
				dataView.dataItems.forEach(dataItem => {
					dataItem.navigationPathId = navPathIds[dataItem.itemId] || null;
				});
			});
			if (localFilters.length) {
				result.localFilters = localFilters;
			}

			return Promise.resolve(result);
		}
	};
});
